import React from 'react';
import { motion } from 'framer-motion';
import { ArrowRight, Star, ChevronDown } from 'lucide-react';
import HeroCandidateCards from './HeroCandidateCards';
import LiveTicker from './LiveTicker';
import CountUp from './CountUp';
import { heroFaces } from '../data/mock';
import { BOOKING_URL, CTA_LABEL } from '../data/constants';

const words = [
  { t: 'Dein' }, { t: 'perfekter' }, { t: 'Kandidat.' }, { br: true },
  { t: 'In' }, { t: '24', gold: true }, { t: 'Stunden.', gold: true }, { br: true },
  { t: 'Zum' }, { t: 'Festpreis.' },
];

const wordAnim = {
  hidden: { opacity: 0, y: 40, rotateX: -40 },
  show: (i) => ({ opacity: 1, y: 0, rotateX: 0, transition: { delay: 0.25 + i * 0.08, type: 'spring', stiffness: 110, damping: 18 } }),
};

const fade = (delay) => ({
  initial: { opacity: 0, y: 24 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.7, ease: [0.2, 0.8, 0.2, 1] },
});

const metrics = [
  { value: 24, suffix: 'h', label: 'bis zum ersten Kandidaten', sub: 'statt 4–12 Wochen' },
  { value: 7999, suffix: ' €', label: 'Festpreis – jede Position', sub: 'statt 20–35 % vom Gehalt' },
  { value: 0, suffix: ' €', label: 'Risiko für dich', sub: 'kostenlose Nachbesetzung' },
];

const HeroSection = () => (
  <section id="home" className="relative bg-obsidian text-white overflow-hidden grain" data-testid="hero-section">
    <div className="absolute inset-0 mesh-grid" />
    <div className="absolute -top-40 -left-32 w-[640px] h-[640px] rounded-full bg-violet-700/30 blur-[140px] light-pool" />
    <div className="absolute top-1/3 right-0 w-[520px] h-[520px] rounded-full bg-indigo-600/20 blur-[140px] light-pool-2" />
    <div className="absolute bottom-0 left-1/3 w-[400px] h-[400px] rounded-full bg-amber-500/10 blur-[120px]" />
    <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-amber-400/60 to-transparent" />

    <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-8 pt-32 lg:pt-40 pb-16 lg:pb-24">
      <div className="grid lg:grid-cols-12 gap-14 lg:gap-10 items-center">
        <div className="lg:col-span-7">
          <motion.div {...fade(0.1)} className="flex flex-wrap items-center gap-3 mb-8" data-testid="hero-eyebrow">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-4 py-1.5 text-xs font-medium text-emerald-300">
              <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" /><span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-400" /></span>
              Kapazität für 3 neue Positionen diese Woche
            </span>
          </motion.div>

          <h1 className="text-5xl sm:text-6xl lg:text-7xl xl:text-[5.4rem] font-extrabold leading-[1.02] tracking-tight" style={{ perspective: 800 }} data-testid="hero-headline">
            {words.map((w, i) =>
              w.br ? <br key={i} /> : (
                <motion.span key={i} custom={i} variants={wordAnim} initial="hidden" animate="show" className={`inline-block mr-[0.22em] ${w.gold ? 'text-gold' : 'text-white'}`}>
                  {w.t}
                </motion.span>
              )
            )}
          </h1>

          <motion.p {...fade(1.0)} className="mt-8 text-base md:text-lg text-slate-300 leading-relaxed max-w-xl" data-testid="hero-subline">
            <span className="text-white font-semibold">HIREKA</span> sucht, interviewt und brieft Fach- und Führungskräfte für Unternehmen jeder Größe – vom Start-up bis zum Konzern.
            Du erhältst innerhalb von 24 Stunden den ersten passenden Kandidaten – für{' '}
            <span className="text-amber-300 font-semibold">7.999 € Festpreis</span> statt 20–35 % vom Jahresgehalt.
            Inklusive kostenloser Nachbesetzung.
          </motion.p>

          <motion.div {...fade(1.15)} className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-4">
            <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="btn-lux px-7 sm:px-9 py-4 sm:py-5 text-base sm:text-lg w-full sm:w-auto" data-testid="hero-cta">
              <span className="whitespace-nowrap">{CTA_LABEL}</span>
              <ArrowRight className="w-5 h-5 flex-shrink-0" />
            </a>
            <a href="#prozess" className="btn-ghost-dark px-7 py-4 sm:py-5 text-base w-full sm:w-auto" data-testid="hero-secondary-cta">
              So funktioniert's
              <ChevronDown className="w-4 h-4" />
            </a>
          </motion.div>

          <motion.div {...fade(1.3)} className="mt-8 flex flex-wrap items-center gap-x-8 gap-y-4" data-testid="hero-social-proof">
            <div className="flex items-center gap-3">
              <div className="flex -space-x-2.5">
                {heroFaces.map((src, i) => (
                  <img key={i} src={src} alt="" className="w-9 h-9 rounded-full object-cover border-2 border-[#0B0A12]" />
                ))}
              </div>
              <div className="text-sm">
                <p className="text-white font-semibold leading-tight">200+ Unternehmen</p>
                <p className="text-slate-400 text-xs">vertrauen HIREKA</p>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="flex gap-0.5">{[1,2,3,4,5].map(i => <Star key={i} className="w-4 h-4 text-amber-400 fill-amber-400" />)}</div>
              <span className="text-white font-semibold">5.0</span>
              <span className="text-slate-400">aus 50+ Bewertungen</span>
            </div>
          </motion.div>
        </div>

        <div className="lg:col-span-5 relative mt-6 lg:mt-0">
          <HeroCandidateCards />
        </div>
      </div>

      <motion.div {...fade(1.5)} className="mt-24 lg:mt-28 grid sm:grid-cols-3 gap-4" data-testid="hero-metrics">
        {metrics.map((m, i) => (
          <div key={i} className="glass-dark rounded-2xl p-6 relative overflow-hidden group hover:border-amber-400/30 transition-colors duration-300">
            <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full bg-violet-600/20 blur-2xl group-hover:bg-amber-400/20 transition-colors duration-500" />
            <div className="font-display text-4xl lg:text-5xl font-extrabold text-white tracking-tight">
              <CountUp to={m.value} suffix={m.suffix} />
            </div>
            <p className="mt-2 text-slate-200 font-medium">{m.label}</p>
            <p className="text-sm text-slate-500">{m.sub}</p>
          </div>
        ))}
      </motion.div>
    </div>

    <LiveTicker />
  </section>
);

export default HeroSection;
